import type React from "react";
import { useEffect } from "react";

import { useTranslation } from "react-i18next";

import { useSessionStore } from "stores/session";

import { setOrgTimezone } from "simplydo/core/dates";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import api from "api";
import util from "utils/utils";

import AppStyledDashboard from "./AppStyledDashboard";
import { Footer } from "./lib/UI";
import { SimpleNavBar } from "./lib/UI/NavBar";

const AppWrapContainer = styled.div`
  flex: 1;
  display: flex;
  min-height: 100%;
  flex-direction: column;
`;

type AppWrapProps = {
  content: React.ReactNode;
  hideName?: boolean;
  fluid?: boolean;
  excludeWelcomeText?: boolean;
  scrolling?: boolean;
  padded?: boolean | "very";
  backgroundColor?: string;
};

const AppWrap = ({
  content,
  hideName,
  fluid,
  excludeWelcomeText,
  scrolling = true,
}: AppWrapProps) => {
  const theme = useTheme();

  const { i18n } = useTranslation();
  // Fetch initial organisation by code if present in the URL
  useEffect(() => {
    const code = util.code();
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get("lang");
    i18n.changeLanguage(lang || "en");
    if (code) {
      api.organisations.getByCode(
        code,
        (newOrganisation) => {
          i18n.changeLanguage(lang || newOrganisation.defaultLanguage || "en");
          setOrgTimezone(newOrganisation.defaultTimezone);
          useSessionStore.setState({ urlOrganisation: newOrganisation });
        },
        () => {},
      );
    } else {
      api.organisations.getByDomain(
        window.location.hostname,
        (newOrganisation) => {
          if (!newOrganisation) {
            return;
          }
          i18n.changeLanguage(lang || newOrganisation.defaultLanguage || "en");
          setOrgTimezone(newOrganisation.defaultTimezone);
          useSessionStore.setState({ urlOrganisation: newOrganisation });
        },
        () => {},
      );
    }
  }, [i18n]);

  return (
    <AppWrapContainer>
      <SimpleNavBar />
      <AppStyledDashboard
        content={content}
        hideName={!!hideName}
        fluid={!!fluid}
        excludeWelcomeText={!!excludeWelcomeText}
        scrolling={scrolling}
      />
      {!theme.sizes.isMobile ? <Footer /> : null}
    </AppWrapContainer>
  );
};

export default AppWrap;
